/**
 * locationUtils.ts
 *
 * Browser geolocation detection, reverse geocoding, timezone detection,
 * and timezone-aware datetime formatting / conversion helpers.
 */

// ---------------------------------------------------------------------------
// Timezone
// ---------------------------------------------------------------------------

/** Returns the IANA timezone string from the browser (e.g. "America/New_York"). */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

// ---------------------------------------------------------------------------
// Geolocation
// ---------------------------------------------------------------------------

export interface GeoPosition {
  latitude: number;
  longitude: number;
}

/**
 * Requests the browser's current GPS position.
 * Rejects if permission is denied or times out (10 s).
 */
export function getBrowserPosition(): Promise<GeoPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      (err) => reject(err),
      { timeout: 10_000, maximumAge: 300_000 }
    );
  });
}

// ---------------------------------------------------------------------------
// Reverse geocoding  (BigDataCloud — free, no API key required)
// ---------------------------------------------------------------------------

export interface ReverseGeoResult {
  countryCode: string; // ISO 3166-1 alpha-2, e.g. "US", "NP"
  countryName: string;
  city?: string;
  stateProvince?: string;
  /** Populated by ipGeolocation() — GPS callers already have coordinates directly. */
  latitude?: number;
  longitude?: number;
}

/**
 * Falls back to IP-based geolocation when the browser denies GPS permission.
 * Uses ipapi.co — free tier, no API key required.
 * ipapi.co returns approximate lat/lng based on IP, so we capture those too.
 */
export async function ipGeolocation(): Promise<ReverseGeoResult> {
  const res = await fetch("https://ipapi.co/json/");
  if (!res.ok) throw new Error("IP geolocation request failed");
  const data = await res.json();
  return {
    countryCode: (data.country_code as string | undefined)?.toUpperCase() ?? "",
    countryName: (data.country_name as string | undefined) ?? "",
    city: (data.city as string | undefined) || undefined,
    stateProvince: (data.region as string | undefined) || undefined,
    latitude: typeof data.latitude === "number" ? data.latitude : undefined,
    longitude: typeof data.longitude === "number" ? data.longitude : undefined,
  };
}

export async function reverseGeocode(
  lat: number,
  lng: number
): Promise<ReverseGeoResult> {
  const url =
    `https://api.bigdatacloud.net/data/reverse-geocode-client` +
    `?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("Reverse geocoding request failed");
  const data = await res.json();
  return {
    countryCode: (data.countryCode as string | undefined)?.toUpperCase() ?? "",
    countryName: (data.countryName as string | undefined) ?? "",
    city:
      (data.city as string | undefined) ||
      (data.locality as string | undefined) ||
      undefined,
    stateProvince:
      (data.principalSubdivision as string | undefined) || undefined,
  };
}

// ---------------------------------------------------------------------------
// Datetime: UTC conversion
// ---------------------------------------------------------------------------

/**
 * Converts a `datetime-local` input value ("YYYY-MM-DDTHH:mm") that the user
 * entered as wall-clock time in `timezone` to a UTC ISO-8601 string.
 *
 * Algorithm:
 *   1. Parse the input as if it were UTC → `fakeUTC`
 *   2. Format `fakeUTC` in `timezone` → `tzLocal`
 *   3. offset = fakeUTC − tzLocal  (positive for UTC− zones, negative for UTC+)
 *   4. result  = fakeUTC + offset
 */
export function localDatetimeToUTC(
  localStr: string,
  timezone: string
): string {
  if (!localStr) return "";
  // Treat the input as UTC for now
  const fakeUTC = new Date(localStr + ":00Z");
  if (isNaN(fakeUTC.getTime())) return "";

  // Find what fakeUTC looks like expressed in the target timezone
  const inTZ = new Intl.DateTimeFormat("sv-SE", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(fakeUTC); // "2024-01-15 09:30:00"

  const tzEquiv = new Date(inTZ.replace(" ", "T") + "Z");
  const offsetMs = fakeUTC.getTime() - tzEquiv.getTime();
  return new Date(fakeUTC.getTime() + offsetMs).toISOString();
}

/**
 * Converts a UTC ISO string to a `datetime-local` input value
 * ("YYYY-MM-DDTHH:mm") expressed in the given timezone.
 * Useful for pre-filling edit forms.
 */
export function utcToLocalDatetime(utcStr: string, timezone: string): string {
  if (!utcStr) return "";
  try {
    const date = new Date(utcStr);
    const formatted = new Intl.DateTimeFormat("sv-SE", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(date); // "2024-01-15 14:30:00"
    // Drop the seconds to match datetime-local format
    return formatted.replace(" ", "T").slice(0, 16); // "2024-01-15T14:30"
  } catch {
    return "";
  }
}

// ---------------------------------------------------------------------------
// Datetime: display formatting
// ---------------------------------------------------------------------------

/**
 * Formats a UTC ISO string for display in the given IANA timezone.
 * Falls back to the browser's local timezone if the timezone is invalid.
 */
export function formatInTimezone(
  dateStr: string,
  timezone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" }
): string {
  try {
    return new Intl.DateTimeFormat("default", {
      ...options,
      timeZone: timezone,
    }).format(new Date(dateStr));
  } catch {
    // Fallback if timezone string is unrecognised
    return new Date(dateStr).toLocaleString();
  }
}

/**
 * Formats a date for display using only date parts (no time) in the given timezone.
 */
export function formatDateInTimezone(
  dateStr: string,
  timezone: string
): string {
  return formatInTimezone(dateStr, timezone, { dateStyle: "medium" });
}

// ---------------------------------------------------------------------------
// Country list (ISO 3166-1 alpha-2) — curated for the Nepali diaspora
// ---------------------------------------------------------------------------

/**
 * Returns the human-readable country name for a given ISO 3166-1 alpha-2 code.
 * Falls back to the code itself if not found.
 */
export function countryNameFromCode(code: string): string {
  return COUNTRIES.find((c) => c.code === code)?.name ?? code;
}

export const COUNTRIES: { code: string; name: string }[] = [
  { code: "NP", name: "Nepal" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "IN", name: "India" },
  { code: "DE", name: "Germany" },
  { code: "JP", name: "Japan" },
  { code: "KR", name: "South Korea" },
  { code: "SG", name: "Singapore" },
  { code: "NZ", name: "New Zealand" },
  { code: "QA", name: "Qatar" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "KW", name: "Kuwait" },
  { code: "BH", name: "Bahrain" },
  { code: "OM", name: "Oman" },
  { code: "MY", name: "Malaysia" },
  { code: "FR", name: "France" },
  { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" },
  { code: "PT", name: "Portugal" },
  { code: "NL", name: "Netherlands" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "DK", name: "Denmark" },
  { code: "FI", name: "Finland" },
  { code: "CH", name: "Switzerland" },
  { code: "AT", name: "Austria" },
  { code: "BE", name: "Belgium" },
  { code: "BR", name: "Brazil" },
  { code: "ZA", name: "South Africa" },
  { code: "LK", name: "Sri Lanka" },
  { code: "BD", name: "Bangladesh" },
  { code: "PK", name: "Pakistan" },
];
